import { useContext, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Typography,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";

import {
  THead,
  TBodyCell,
  TBodyCellCheckBox,
  TPagination,
} from "src/app/@core/forms/dataTable/dataTable.control";
import { stableSort, isUoN, toBool, toMMDDYYYY } from "src/app/@core/common";
import { ModuleContext } from "src/app/@core/@contexts/module.context";
import { DomainContext } from "src/app/@core/@contexts/domain.context";

import { TextInput } from "src/app/@core/forms/controls/textInput";
import { NoWrapControl } from "src/app/@core/forms/controls/noWrapControl";
import { NoDataFound } from "src/app/@core/forms/controls/noDataFound";

import {
  UserEditDialogModel,
  UserEditDialogController,
  UserEdit,
} from "./userManagement.edit";
import {
  UserAddDialogModel,
  UserAddDialogController,
  UserAdd,
} from "./userManagement.add";
import { display, margin } from "@mui/system";

/** Search Criteria Component */
const SearchCriteria = () => {
  const frmCriteria = useForm({
    mode: "onChange",
    defaultValues: { firstName: "", lastName: "", retailerName: "" },
  });

  const { setSearchCriteria } = useContext(ModuleContext);
  const { control, getValues } = frmCriteria;

  /**
   *
   */
  const onSearch = async () => {
    const criteria = getValues();
    setSearchCriteria(criteria);
  };

  return (
    <FormProvider {...frmCriteria}>
      <TextInput id="lastName" label="Last Name" control={control} />
      <TextInput id="firstName" label="First Name" control={control} />
      <TextInput id="retailerName" label="Retailer Name" control={control} />
      <Button
        variant="contained"
        color="secondary"
        onClick={onSearch}
        style={{ marginTop: 7 }}
      >
        Search
      </Button>
    </FormProvider>
  );
};

/** Header Component */
export const ContactsHeader = () => {
  return (
    <div style={{ width: "100%" }}>
      <div className="flex flex-col sm:flex-row space-y-16 sm:space-y-0 flex-1 w-full items-center justify-between container">
        <Typography className="text-24 md:text-32 font-extrabold tracking-tight">
          User Management
        </Typography>
      </div>

      <div style={{ marginBottom: 10 }}>
        <hr />
        <div className="flex flex-row container" style={{ marginTop: 10 }}>
          <SearchCriteria />
        </div>
      </div>
    </div>
  );
};

/** User Management Table */
export const UserManagementTable = (props) => {
  const { gvItems, retailers } = props;
  const { api, spinner } = useContext(DomainContext);
  const { loadModelDataFunc, page, rowsPerPage } = useContext(ModuleContext);

  const [sortOrder, setSortOrder] = useState({ id: null, direction: "asc" });
  const [userEditDialogModel, setUserEditDialogModel] = useState(
    new UserEditDialogModel()
  );
  const [userAddDialogModel, setUserAddDialogModel] = useState(
    new UserAddDialogModel()
  );

  const userEditDialogModelController = new UserEditDialogController(
    userEditDialogModel,
    setUserEditDialogModel
  );

  const userAddDialogModelController = new UserAddDialogController(
    userAddDialogModel,
    setUserAddDialogModel
  );

  /**
   *
   * @param {*} prop
   */
  const onSort = (prop) => {
    const direction =
      sortOrder.id === prop && sortOrder.direction === "desc" ? "asc" : "desc";
    setSortOrder({ id: prop, direction });
  };

  /**
   *
   */
  const onAdd = async () => {
    const result = await userAddDialogModelController.show(retailers);
    if (result) loadModelDataFunc();
  };

  /**
   *
   * @param {*} event
   * @param {*} value
   * @returns
   */
  const onChangePage = (event, value) => page(value);

  /**
   *
   * @param {*} event
   * @returns
   */
  const onChangeRowsPerPage = (event) => rowsPerPage(event.target.value);

  /**
   *
   * @param {*} id
   * @returns
   */
  const getEndUser = async (id) => {
    let result;
    spinner.show();
    try {
      result = await api.contactApi.getEndUser(id);
    } catch (err) {
      console.log(err);
    } finally {
      spinner.hide();
    }
    return result;
  };

  /**
   *
   * @param {*} item
   */
  const onEdit = async (item) => {
    const endUser = await getEndUser(item.contactID);
    const retailerSelected = retailers.find((r) => r.id === endUser.retailerID);
    const result = await userEditDialogModelController.show(
      endUser,
      retailers,
      retailerSelected
    );
    if (result) loadModelDataFunc();
  };

  /**
   *
   * @param {*} status
   * @param {*} id
   */
  const setStatus = async (status, id) => {
    const result = await api.contactApi.get(id);
    const obj = { ...result, isActive: status };
    await api.contactApi.update(obj);
    await loadModelDataFunc();
  };

  // if (isUoN(gvItems) || gvItems.length === 0) return <NoDataFound />;

  const columns = [
    {
      id: "action",
      align: "center",
      disablePadding: false,
      sort: false,
      width: 100,
    },
    {
      id: "allowLogin",
      align: "center",
      disablePadding: false,
      label: "Allow Login",
      sort: true,
      width: 100,
    },
    {
      id: "lastName",
      align: "left",
      disablePadding: false,
      label: "LastName",
      sort: true,
      width: 200,
    },
    {
      id: "firstName",
      align: "left",
      disablePadding: false,
      label: "FirstName",
      sort: true,
      width: 200,
    },
    {
      id: "retailerName",
      align: "left",
      disablePadding: false,
      label: "Retailer Name",
      sort: true,
      width: 0,
    },
    {
      id: "email",
      align: "left",
      disablePadding: false,
      label: "Email",
      sort: true,
      width: 400,
    },
    {
      id: "dateCreated",
      align: "left",
      disablePadding: false,
      label: "Date Created",
      sort: true,
      width: 100,
    },
  ];

  return (
    <div className="w-full flex flex-col min-h-full">
      <UserEdit controller={userEditDialogModelController} />
      <UserAdd controller={userAddDialogModelController} />

      <Paper sx={{ width: "100%", mb: 2, boxShadow: "none !important" }}>
        <div style={{ display: "flex", justifyContent: "end" }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={onAdd}
            className="btn-export mb-[12px] mx-[12px]"
            startIcon={<FuseSvgIcon>heroicons-outline:plus</FuseSvgIcon>}
          >
            Create
          </Button>

          {isUoN(gvItems) ||
            (gvItems.length > 0 && (
              <TPagination
                gvItems={gvItems}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={onChangePage}
                onChangeRowsPerPage={onChangeRowsPerPage}
              />
            ))}
        </div>

        <Table
          stickyHeader
          className="min-w-xl"
          size="small"
          aria-labelledby="tableTitle"
        >
          <THead columns={columns} sortOrder={sortOrder} onSort={onSort} />
          <TableBody>
            {isUoN(gvItems) || gvItems.length > 0 ? (
              stableSort(gvItems, sortOrder.id, sortOrder.direction)
                .slice(
                  page() * rowsPerPage(),
                  page() * rowsPerPage() + rowsPerPage()
                )
                .map((item) => (
                  <TableRow hover tabIndex={-1} key={item.itemID}>
                    <TableCell
                      className="cell-border-top cell-border-right"
                      component="td"
                      scope="row"
                      align="center"
                    >
                      <FuseSvgIcon
                        className="text-48 cursor-pointer"
                        style={{ display: "block", margin: "0 auto" }}
                        size={20}
                        color="action"
                        onClick={() => onEdit(item)}
                      >
                        material-solid:edit
                      </FuseSvgIcon>
                    </TableCell>
                    <TBodyCellCheckBox align="center" value={item.allowLogin} />
                    <TBodyCell align="left" value={item.lastName} />
                    <TBodyCell align="left" value={item.firstName} />
                    <TBodyCell align="left" value={item.retailerName} />
                    <TBodyCell align="left" value={item.email} />
                    <TBodyCell
                      align="left"
                      value={toMMDDYYYY(item.dateCreated)}
                    />
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <NoDataFound />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
};
